import React from "react";
import { createBaseBlock } from "./BaseBlock";
import { Typography } from "antd";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { stripMarkdown } from "../cardUtils";
import { Tags } from "../components/Tags";
import { ActivityItem } from "../components/ActivityItem";

interface ActivityData {
  headline: string;
  description: string;
  type: string;
  impact_type: string;
  contributors: string[];
  evidence: string[];
}

// TopActivities Block Recipe
const topActivitiesRecipe = {
  id: "top-activities-block",
  name: "Top Activities Block",
  globalKey: [],
  prefix: `[
        {
          "headline": "string — what area of work this applies in 1-3 words (e.g. 'Security', 'Terraform Migration')",
          "description": "string — what happened and why it mattered. include reference to the work like PRs, issues, etc.. ",
          "type": "string — e.g. 'issue', 'pull_request', 'theme'",
          "impact_type": "string — e.g. 'quality', 'risk', 'collaboration'",
          "contributors": ["string — names involved"],
          "evidence": ["string — supporting PRs, issues, themes, metrics"]
        }
      ]`,
  format: {
    type: "json",
    fields: ["description"],
    constraints: [{ field: "description", type: "string", required: true }],
  },
  validationRules: [
    {
      type: "json_structure",
      condition: (data: any): boolean => {
        try {
          const parsed = JSON.parse(data) as ActivityData[];
          return (
            Array.isArray(parsed) && parsed.every((item) => item.description)
          );
        } catch {
          return false;
        }
      },
      message: "Response must be valid JSON with required fields",
    },
  ],
};

// TopActivities Block Parser
function topActivitiesParser(data: any) {
  try {
    return {
      data: data[topActivitiesBlock.id],
    };
  } catch (error) {
    return {
      error: true,
      message: `Failed to parse top activities data: ${
        error instanceof Error ? error.message : String(error)
      }`,
      rawContent: data,
    };
  }
}

// TopActivities Block Variants
const topActivitiesVariants = [
  {
    id: "default",
    name: "Default",
    description: "Standard activity list display",
  },
  {
    id: "compact",
    name: "Compact",
    description: "Compact activity list display",
  },
  {
    id: "list",
    name: "List",
    description: "List activity list display",
  },
  {
    id: "minimal",
    name: "Minimal",
    description: "Minimal activity list display",
  },
  {
    id: "top3",
    name: "Top 3",
    description: "Shows only top 3 activities in a list format",
  },
];

// TopActivities Block Renderer
function TopActivitiesBlockRenderer(props: any) {
  const { data, variant, className, style } = props;
  if (data === undefined) {
    return null;
  }
  const activitiesData = data[topActivitiesBlock.id];

  if (!activitiesData || activitiesData.length === 0) return null;

  const renderDefaultCard = (activitiesData: ActivityData[]) => (
    <div className="responsive-card-grid">
      {activitiesData.map((activity: ActivityData, idx: number) => (
        <ActivityItem
          key={`activity-${idx}`}
          description={activity.description}
          title={activity.headline}
          tags={activity.contributors}
          cta={activity.impact_type}
          className="vzbl-panel-overlay vzbl-panel-shadow"
          variant="default"
        />
      ))}
    </div>
  );

  const renderCompactCard = (activitiesData: ActivityData[]) => (
    <div className="responsive-card-grid">
      {activitiesData.map((activity: ActivityData, idx: number) => (
        <div key={`activity-${idx}`} style={{ height: "100%", width: "100%" }}>
          <div
            className="vzbl-panel-shadow vzbl-panel-overlay"
            style={{ padding: "var(--padding-3)" }}
          >
            <Typography.Text className="vzbl-header-4">
              {stripMarkdown(activity.headline)}
            </Typography.Text>
            <Typography.Text className="vzbl-text-4">
              <Markdown
                remarkPlugins={[remarkGfm]}
                skipHtml={true}
                className="vzbl-text-3 double-line"
              >
                {activity.description}
              </Markdown>
            </Typography.Text>
            <div style={{ width: "100%" }}>
              <Tags tags={activity.contributors} tagClassName="vzbl-pill-xsm" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderListCard = (activitiesData: ActivityData[]) => (
    <div className="responsive-card-grid">
      <ul>
        {activitiesData.map((activity, idx) => (
          <li key={`activity-${idx}`} className="vzbl-text-3">
            <Markdown remarkPlugins={[remarkGfm]} skipHtml={true}>
              {activity.description}
            </Markdown>
          </li>
        ))}
      </ul>
    </div>
  );

  const renderMinimalCard = (activitiesData: ActivityData[]) => (
    <div className="responsive-card-grid">
      {activitiesData.map((activity: ActivityData, idx: number) => (
        <div key={`activity-${idx}`} style={{ height: "100%", width: "100%" }}>
          <div
            className="vzbl-panel-shadow vzbl-panel-overlay"
            style={{ padding: "var(--padding-3)" }}
          >
            <Typography.Text className="vzbl-header-4">
              {stripMarkdown(activity.headline)}
            </Typography.Text>
            <Typography.Text className="vzbl-text-4">
              <Markdown
                remarkPlugins={[remarkGfm]}
                skipHtml={true}
                className="vzbl-text-3 double-line"
              >
                {activity.description}
              </Markdown>
            </Typography.Text>
          </div>
        </div>
      ))}
    </div>
  );

  const renderTop3Card = (activitiesData: ActivityData[]) => (
    <div className="responsive-card-grid">
      <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
        {activitiesData
          .slice(0, 3)
          .map((activity: ActivityData, idx: number) => (
            <li
              key={`activity-${idx}`}
              style={{ marginBottom: "var(--padding-4)" }}
            >
              <Typography.Text
                className="vzbl-header-5"
                style={{ display: "block", marginBottom: 4 }}
              >
                {stripMarkdown(activity.headline)}
              </Typography.Text>
              <Typography.Text className="vzbl-text-3">
                <Markdown remarkPlugins={[remarkGfm]} skipHtml={true}>
                  {activity.description}
                </Markdown>
              </Typography.Text>
            </li>
          ))}
      </ul>
    </div>
  );

  const getCardRenderer = () => {
    switch (variant) {
      case "compact": {
        return renderCompactCard;
      }
      case "minimal": {
        return renderMinimalCard;
      }
      case "list": {
        return renderListCard;
      }
      case "top3": {
        return renderTop3Card;
      }
      default: {
        return renderDefaultCard;
      }
    }
  };
  const cardRenderer = getCardRenderer();
  return (
    <>
      {activitiesData && activitiesData.length > 0 && (
        <div className="vzbl-panel-container">
          {variant !== "top3" && (
            <Typography.Title level={4} className="vzbl-header-4">
              Top Activities
            </Typography.Title>
          )}
          {cardRenderer(activitiesData)}
        </div>
      )}
    </>
  );
}

// Create TopActivities Block
const topActivitiesBlock = createBaseBlock({
  id: "top-activities",
  name: "Top Activities Block",
  description: "Displays a list of top activities",
  recipe: topActivitiesRecipe,
  variants: topActivitiesVariants,
  parser: topActivitiesParser,
  renderer: TopActivitiesBlockRenderer,
});

// Export the block for direct use
export { topActivitiesBlock };
